import { Banner, useGetBannerQuery } from '@/data/apis/admin';
import { COUNTRY_MALAYSIA } from '@/data/constants';
import { Entity, Locale } from '@/data/enums';
import { Product, ProductEnums } from '@/data/enums/product';
import { useRoute } from './useRoute';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const PRODUCT_BANNER_CONFIG = [
  {
    entity: Entity.TAKAFUL,
    productType: Product.iMedicalCard,
    isMobile: false,
    language: Locale.EN,
    labelName: ProductEnums.IMedicalCardEliteTakafulEn,
  },
  {
    entity: Entity.TAKAFUL,
    productType: Product.iMedicalCard,
    isMobile: false,
    language: Locale.MY,
    labelName: ProductEnums.IMedicalCardEliteTakafulMy,
  },
  {
    entity: Entity.TAKAFUL,
    productType: Product.iMedicalCard,
    isMobile: true,
    language: Locale.EN,
    labelName: ProductEnums.IMedicalCardEliteTakafulEnMobile,
  },
  {
    entity: Entity.TAKAFUL,
    productType: Product.iMedicalCard,
    isMobile: true,
    language: Locale.MY,
    labelName: ProductEnums.IMedicalCardEliteTakafulMyMobile,
  },
  {
    entity: Entity.INSURANCE,
    productType: Product.EzyMedical,
    isMobile: false,
    language: Locale.EN,
    labelName: ProductEnums.EzyMedicalInsuranceEn,
  },
  {
    entity: Entity.INSURANCE,
    productType: Product.EzyMedical,
    isMobile: false,
    language: Locale.MY,
    labelName: ProductEnums.EzyMedicalInsuranceMy,
  },
  {
    entity: Entity.INSURANCE,
    productType: Product.EzyMedical,
    isMobile: true,
    language: Locale.EN,
    labelName: ProductEnums.EzyMedicalInsuranceEnMobile,
  },
  {
    entity: Entity.INSURANCE,
    productType: Product.EzyMedical,
    isMobile: true,
    language: Locale.MY,
    labelName: ProductEnums.EzyMedicalInsuranceMyMobile,
  },
];

function getProductBanner(
  entity: string,
  language: string,
  product: string,
  isMobile: boolean,
) {
  const match = PRODUCT_BANNER_CONFIG.find(
    (item) =>
      item.entity === entity &&
      item.language === language &&
      item.productType === product &&
      item.isMobile === isMobile,
  );
  return match?.labelName ?? '';
}

function getActiveBanner(banners: Banner[]) {
  if (!banners?.length) return null;

  const latestBanner = banners[banners.length - 1];

  const normalizeDate = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const today = normalizeDate(new Date());
  const startDate = latestBanner.startDate
    ? normalizeDate(new Date(latestBanner.startDate))
    : null;
  const endDate = latestBanner.endDate
    ? normalizeDate(new Date(latestBanner.endDate))
    : null;

  if (startDate && endDate && today >= startDate && today <= endDate) {
    return latestBanner;
  }

  return null;
}

type UseBannerDataOptions = {
  isMobile?: boolean | null;
  /** Whether this is affiliate traffic (from URL query param) */
  isAffiliate?: boolean;
  /** Agent code for affiliate banner lookup */
  agentCode?: string;
};

export function useBannerData({
  isMobile,
  isAffiliate = false,
  agentCode = '',
}: UseBannerDataOptions) {
  const { product, entity } = useRoute();
  const { i18n } = useTranslation();

  const bannerProduct = useMemo(() => {
    return getProductBanner(entity, i18n.language, product, isMobile ?? false);
  }, [entity, i18n.language, product, isMobile]);

  const { data, isLoading, isError } = useGetBannerQuery(
    {
      product: bannerProduct,
      page: 'Quotation',
      isAffiliate,
      countryCode: COUNTRY_MALAYSIA,
      agentCode,
    },
    {
      skip: isMobile === null,
    },
  );

  const activeBanner = useMemo(() => {
    if (isLoading || isError || !data?.data?.length) return null;
    return getActiveBanner(data.data);
  }, [data, isLoading, isError]);

  const campaignTncDoc = useMemo(() => {
    return activeBanner?.tncDoc ?? null;
  }, [activeBanner]);

  return {
    data,
    isLoading,
    isError,
    activeBanner,
    campaignTncDoc,
    bannerProduct,
  };
}
